import { FC, useState, useEffect, createContext, useContext, useRef, useCallback, Dispatch, SetStateAction } from 'react'
import {LayoutSplashScreen} from '../../../../_metronic/layout/core'
import {AuthModel, UserModel} from './_models'
import * as authHelper from './AuthHelpers'
import {getUserByToken} from './_requests'
import {getGuidelines} from './../../../api';
import {WithChildren} from '../../../../_metronic/helpers'
import * as API from '../../../../app/api';
import {type Permission} from '../../../pages/permissions/permissionMetadata'
import {permissionsFromGroups} from './permissionSnapshot'

import { createStore } from 'redux'


type AuthContextProps = {
	auth: AuthModel | undefined
	saveAuth: (auth: AuthModel | undefined) => void
	currentUser: UserModel | undefined
	setCurrentUser: Dispatch<SetStateAction<UserModel | undefined>>
	logout: () => void;
	permissions: any[];
	featureCatalog: Permission[];
	permissionsLoaded: boolean;
	permissionsError: string;
	refreshPermissions: (force?: boolean) => Promise<boolean>;
	distributors: any[]
	reportFilters: any[]
	industries: any[]
	industriesName: any[]
	regions: any[]
	bu: any[]
	plataformas: any[]
	loadDistributors: () => Promise<void>
	loadReportFilters: () => Promise<void>
	loadIndustries: () => Promise<void>
	loadRegions: () => Promise<void>
	loadBusinessUnits: () => Promise<void>
	loadPlataformas: () => Promise<void>
}

type ScopedResource =
	| 'distributors'
	| 'reportFilters'
	| 'industries'
	| 'regions'
	| 'businessUnits'
	| 'plataformas'

const initAuthContextPropsState = {
	auth: authHelper.getAuth(),
	saveAuth: () => {},
	currentUser: undefined,
	setCurrentUser: () => {},
	logout: () => {},
	permissions: [],
	featureCatalog: [],
	permissionsLoaded: false,
	permissionsError: '',
	refreshPermissions: async () => false,
	distributors: [],
	reportFilters: [],
	industries: [],
	industriesName: [],
	regions: [],
	bu: [],
	plataformas: [],
	loadDistributors: async () => {},
	loadReportFilters: async () => {},
	loadIndustries: async () => {},
	loadRegions: async () => {},
	loadBusinessUnits: async () => {},
	loadPlataformas: async () => {},
}

const AuthContext = createContext<AuthContextProps>(initAuthContextPropsState)

const useAuth = () => {
	return useContext(AuthContext)
}

const AuthProvider: FC<WithChildren> = ({children}) => {
	const [auth, setAuth] = useState<AuthModel | undefined>(authHelper.getAuth())
	const [currentUser, setCurrentUser] = useState<UserModel | undefined>()
	const [permissions, setPermissions] = useState<any[]>([]);
	const [featureCatalog, setFeatureCatalog] = useState<Permission[]>([])
	const [permissionsLoaded, setPermissionsLoaded] = useState(false)
	const [permissionsError, setPermissionsError] = useState('')
	const permissionRevision = useRef(0)
	const permissionRequest = useRef<{token: string; promise: Promise<boolean>} | null>(null)
	const [distributors, setDistributors] = useState<any[]>([])
	const [reportFilters, setReportFilters] = useState<any[]>([])
	const [industries, setIndustries] = useState<any[]>([])
	const [industriesName, setIndustriesName] = useState<any[]>([])
	const [regions, setRegions] = useState<any[]>([])
	const [bu, setBUs] = useState<any[]>([])
	const [plataformas, setPlataformas] = useState<any[]>([])

	const requestedResources = useRef<Set<ScopedResource>>(new Set())

	const loadResourceOnce = useCallback(async (
		resource: ScopedResource,
		request: () => Promise<void>
	) => {
		if (requestedResources.current.has(resource)) return

		requestedResources.current.add(resource)
		try {
			await request()
		} catch (error) {
			requestedResources.current.delete(resource)
			console.error(`Erro ao carregar ${resource}:`, error)
		}
	}, [])

	const saveAuth = (auth: AuthModel | undefined) => {
		setAuth(auth)
		if (auth) {
			authHelper.setAuth(auth)
		} else {
			authHelper.removeAuth()
		}
	}

	const logout = () => {
		saveAuth(undefined)
		setCurrentUser(undefined)
		setPermissions([])
		setFeatureCatalog([])
		setPermissionsLoaded(false)
		setPermissionsError('')
		permissionRevision.current++
		permissionRequest.current = null
		setDistributors([])
		setReportFilters([])
		setIndustries([])
		setIndustriesName([])
		setRegions([])
		setBUs([])
		setPlataformas([])
		requestedResources.current.clear()
		localStorage.removeItem("current_company");
	}

	const refreshPermissions = useCallback((force = false): Promise<boolean> => {
		const token = auth?.api_token
		if (!token) return Promise.resolve(false)
		if (!force && permissionRequest.current?.token === token) return permissionRequest.current.promise
		const revision = ++permissionRevision.current
		const promise = (async () => {
			try {
				// Usa os mesmos endpoints que já carregavam o menu antes do controle de status.
				const [catalog, groups] = await Promise.all([API.getPermissions(), API.getGroupPermissions()])
				if (!catalog.success || !groups.success || !Array.isArray(catalog.data) || !Array.isArray(groups.data)) {
					throw new Error('Resposta de permissões inválida.')
				}
				if (revision !== permissionRevision.current) return false
				const data = permissionsFromGroups(catalog.data, groups.data, getUserData()?.permissions)
				setPermissions((previous) => JSON.stringify(previous) === JSON.stringify(data) ? previous : data)
				setFeatureCatalog((previous) => JSON.stringify(previous) === JSON.stringify(catalog.data) ? previous : catalog.data)
				setPermissionsError('')
				return true
			} catch (error) {
				if (revision === permissionRevision.current) {
					setPermissionsError('Não foi possível validar suas permissões. Tente novamente.')
				}
				return false
			} finally {
				if (revision === permissionRevision.current) {
					setPermissionsLoaded(true)
					permissionRequest.current = null
				}
			}
		})()
		permissionRequest.current = {token, promise}
		return promise
	}, [auth?.api_token])

	useEffect(() => {
		setPermissions([])
		setFeatureCatalog([])
		setPermissionsLoaded(false)
		setPermissionsError('')
		if (!auth?.api_token) return
		void refreshPermissions()
		const refresh = () => { void refreshPermissions() }
		window.addEventListener('focus', refresh)
		const interval = window.setInterval(refresh, 60000)
		return () => {
			window.removeEventListener('focus', refresh)
			window.clearInterval(interval)
			permissionRevision.current++
			permissionRequest.current = null
		}
	}, [auth?.api_token, refreshPermissions])

	useEffect(() => {
		// AuthInit pode terminar depois da primeira consulta. Atualiza após carregar
		// o perfil, sem deixar uma resposta anterior apagar os acessos da sessão.
		if (currentUser && auth?.api_token) void refreshPermissions(true)
	}, [currentUser, auth?.api_token, refreshPermissions])

	const loadDistributors = useCallback(() => loadResourceOnce('distributors', async () => {
		const result = await API.getDistributors()
		if (!result.success) return

		const data: any[] = result.data.map((item: any) => ({
			value: item[0],
			label: item[1],
		}))
		setDistributors(data)
	}), [loadResourceOnce])

	const loadReportFilters = useCallback(() => loadResourceOnce('reportFilters', async () => {
		const response = await API.getReportFilters()
		const data:any = { politicaPreco:[], grupo:[], familia:[], marcas:[], tipoPrecos:[]};
		if (response.success) {
			data.politicaPreco = response.data.politicaPreco.map((item: any) => ({
				value: item,
				label: item,
			}));
			data.grupo = response.data.grupo.map((item: any) => ({
				value: item,
				label: item,
			}));
			data.familia = response.data.familia.map((item: any) => ({
				value: item,
				label: item,
			}));
			data.marcas = response.data.marca.map((item: any) => ({
				value: item,
				label: item,
			}));
			data.tipoPrecos = response.data.tipoPreco.map((item: any) => ({
				value: item,
				label: item,
			}));
			setReportFilters(data);
		}
	}), [loadResourceOnce])
	
	const loadIndustries = useCallback(() => loadResourceOnce('industries', async () => {
		const result = await API.getIndustries()
		if (result.success) {
			const industryOptions: any[] = result.data.map((item: any) => ({
				value: item[0],
				label: item[1],
			}))
			const industryNameOptions: any[] = result.data.map((item: any) => ({
				value: item[1],
				label: item[1],
			}))
			setIndustries(industryOptions)
			setIndustriesName(industryNameOptions)
		}
	}), [loadResourceOnce])
	
	const loadRegions = useCallback(() => loadResourceOnce('regions', async () => {
		const result = await API.getRegions()
		if (result.success) {
			const data: any[] = result.data.map((item: any) => ({
				value: item[0],
				label: item[0],
			}))
			setRegions(data)
		}
	}), [loadResourceOnce])

	const loadPlataformas = useCallback(() => loadResourceOnce('plataformas', async () => {
		const result = await API.getPlataformas()
		if (result.success) {
			const data: any[] = result.data.map((item: any) => ({
				value: item[0],
				label: item[0],
			}))
			setPlataformas(data)
		}
	}), [loadResourceOnce])

	const loadBusinessUnits = useCallback(() => loadResourceOnce('businessUnits', async () => {
		const result = await API.getBusinessUnits()
		if (result.success) {
		  const data: any[] = []
		  result.data.map((item: any) => {
			if ((item[0] && item[0].trim() == '') || !item[0]) return;
			data.push({
			  value: item[0],
			  label: item[0],
			})
		  })
		  setBUs(data);
		}
	}), [loadResourceOnce])

	return (
		<AuthContext.Provider
		  	value={{
				auth,
				saveAuth,
				currentUser,
				setCurrentUser,
				logout,
				permissions,
				featureCatalog,
				permissionsLoaded,
				permissionsError,
				refreshPermissions,
				distributors,
				reportFilters,
				industries,
				industriesName,
				regions,
				bu,
				plataformas,
				loadDistributors,
				loadReportFilters,
				loadIndustries,
				loadRegions,
				loadBusinessUnits,
				loadPlataformas
			}}
		>
		  {children}
		</AuthContext.Provider>
	);
}

const AuthInit: FC<WithChildren> = ({children}) => {
  // const [guidelines, setGuidelines] = useState(null);
	const {auth, logout, setCurrentUser} = useAuth()
	const didRequest = useRef(false)
	const [showSplashScreen, setShowSplashScreen] = useState(true)
	
	useEffect(() => {
		const requestUser = async (apiToken: string) => {
			try {
				if (!didRequest.current) {
					const response:any = await getUserByToken(apiToken)
					if (response.data.success) {
						localStorage.setItem('auth', JSON.stringify(response.data.data));
						setCurrentUser(response.data);
					}
				}
			} catch (error) {
				if (!didRequest.current) {
					logout()
				}
			} finally {
				setShowSplashScreen(false)
			}
			return () => (didRequest.current = true)
		}
		if (auth && auth.api_token) {
			requestUser(auth.api_token)
		} else {
			logout()
			setShowSplashScreen(false)
		}
	}, []); 

	

	return showSplashScreen ? <LayoutSplashScreen /> : <>{children}</>
}



const getUserData = () => {
	const authData:string = localStorage.getItem('auth') as string;
	try {
		return JSON.parse(authData);
	} catch (e){
		console.log(e);
	}
	return {};
}

const isAdmin = () => {
	const data = getUserData();
	return data && (data.is_admin || data.is_superadmin);
}

const isSuperAdmin = () => {
	const data = getUserData();
	return data && data.is_superadmin;
}

const GetPermissions = () => {
	const { permissions } = useContext(AuthContext);
	return permissions;
};

const useReportFilters = (enabled = true) => {
	const {reportFilters, loadReportFilters} = useContext(AuthContext)
	useEffect(() => {
		if (enabled) void loadReportFilters()
	}, [enabled, loadReportFilters])
	return reportFilters
}

const useDistributors = (enabled = true) => {
	const {distributors, loadDistributors} = useContext(AuthContext)
	useEffect(() => {
		if (enabled) void loadDistributors()
	}, [enabled, loadDistributors])
	return distributors
}
  
const useIndustries = (enabled = true) => {
	const {industries, loadIndustries} = useContext(AuthContext)
	useEffect(() => {
		if (enabled) void loadIndustries()
	}, [enabled, loadIndustries])
	return industries
}

const useIndustriesName = (enabled = true) => {
	const {industriesName, loadIndustries} = useContext(AuthContext)
	useEffect(() => {
		if (enabled) void loadIndustries()
	}, [enabled, loadIndustries])
	return industriesName
}
  
const useRegions = (enabled = true) => {
	const {regions, loadRegions} = useContext(AuthContext)
	useEffect(() => {
		if (enabled) void loadRegions()
	}, [enabled, loadRegions])
	return regions
}
  
const useBUs = (enabled = true) => {
	const {bu, loadBusinessUnits} = useContext(AuthContext)
	useEffect(() => {
		if (enabled) void loadBusinessUnits()
	}, [enabled, loadBusinessUnits])
	return bu
}
  
const usePlataformas = (enabled = true) => {
	const {plataformas, loadPlataformas} = useContext(AuthContext)
	useEffect(() => {
		if (enabled) void loadPlataformas()
	}, [enabled, loadPlataformas])
	return plataformas
}

export {AuthProvider, AuthInit, useAuth, getUserData, isAdmin, isSuperAdmin, GetPermissions, usePlataformas, useDistributors, useReportFilters, useIndustries, useIndustriesName, useRegions, useBUs }
